import { Controller } from "@hotwired/stimulus"

const PUBLIC_KEY_COOKIE = "rsa_public_key"
const PRIVATE_KEY_COOKIE = "rsa_private_key"

// Reads a cookie value by name
function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"))
    return match ? match[1] : null
}

// Imports an RSA public key (Base64-encoded SPKI DER) for encryption (OAEP + SHA-256)
async function importPublicKey(b64: string): Promise<CryptoKey> {
    const der = Uint8Array.from(atob(b64), c => c.charCodeAt(0))
    return crypto.subtle.importKey(
        "spki",
        der.buffer,
        { name: "RSA-OAEP", hash: "SHA-256" },
        false,
        ["encrypt"]
    )
}

// Imports an RSA private key (Base64-encoded PKCS#8 DER) for decryption (OAEP + SHA-256)
async function importPrivateKey(b64: string): Promise<CryptoKey> {
    const der = Uint8Array.from(atob(b64), c => c.charCodeAt(0))
    return crypto.subtle.importKey(
        "pkcs8",
        der.buffer,
        { name: "RSA-OAEP", hash: "SHA-256" },
        false,
        ["decrypt"]
    )
}

// Hybrid encryption: AES-GCM key encrypted with RSA-OAEP
// Format: Base64( [2 bytes: RSA-encrypted key length] + [RSA-encrypted AES key] + [12 bytes IV] + [AES-GCM ciphertext] )
async function encryptHybrid(publicKey: CryptoKey, plaintext: string): Promise<string> {
    // Generate a random AES-256 key
    const aesKey = await crypto.subtle.generateKey(
        { name: "AES-GCM", length: 256 },
        true,
        ["encrypt", "decrypt"]
    )

    // Export AES key as raw bytes and encrypt with RSA-OAEP
    const aesKeyRaw = await crypto.subtle.exportKey("raw", aesKey)
    const encryptedAesKey = await crypto.subtle.encrypt(
        { name: "RSA-OAEP" },
        publicKey,
        aesKeyRaw
    )

    // Encrypt plaintext with AES-GCM
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const encoded = new TextEncoder().encode(plaintext)
    const ciphertext = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        aesKey,
        encoded
    )

    // Pack: [2 bytes length of RSA-encrypted key] + [RSA-encrypted key] + [IV] + [ciphertext]
    const encKeyBytes = new Uint8Array(encryptedAesKey)
    const keyLen = encKeyBytes.length
    const packed = new Uint8Array(2 + keyLen + 12 + ciphertext.byteLength)
    packed[0] = (keyLen >> 8) & 0xff
    packed[1] = keyLen & 0xff
    packed.set(encKeyBytes, 2)
    packed.set(iv, 2 + keyLen)
    packed.set(new Uint8Array(ciphertext), 2 + keyLen + 12)

    return btoa(String.fromCharCode(...packed))
}

// Hybrid decryption: unpacks the format produced by encryptHybrid
async function decryptHybrid(privateKey: CryptoKey, cipherB64: string): Promise<string> {
    const packed = Uint8Array.from(atob(cipherB64), c => c.charCodeAt(0))

    const keyLen = (packed[0] << 8) | packed[1]
    const encryptedAesKey = packed.slice(2, 2 + keyLen)
    const iv = packed.slice(2 + keyLen, 2 + keyLen + 12)
    const ciphertext = packed.slice(2 + keyLen + 12)

    // Decrypt AES key with RSA-OAEP
    const aesKeyRaw = await crypto.subtle.decrypt(
        { name: "RSA-OAEP" },
        privateKey,
        encryptedAesKey
    )

    // Import AES key
    const aesKey = await crypto.subtle.importKey(
        "raw",
        aesKeyRaw,
        { name: "AES-GCM" },
        false,
        ["decrypt"]
    )

    // Decrypt ciphertext with AES-GCM
    const plaintext = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv },
        aesKey,
        ciphertext
    )

    return new TextDecoder().decode(plaintext)
}

export default class PrivateNoteController extends Controller {
    static targets = ["textarea", "saveBtn", "indicator", "noKey", "status"]

    declare readonly textareaTarget: HTMLTextAreaElement
    declare readonly saveBtnTarget: HTMLButtonElement
    declare readonly indicatorTarget: HTMLElement
    declare readonly noKeyTarget: HTMLElement
    declare readonly statusTarget: HTMLElement

    private publicKey: CryptoKey | null = null
    private privateKey: CryptoKey | null = null
    private savedContent: string = ""

    async connect(): Promise<void> {
        await this.loadKeys()
    }

    private async loadKeys(): Promise<void> {
        let publicKeyPem = getCookie(PUBLIC_KEY_COOKIE)
        let privateKeyPem = getCookie(PRIVATE_KEY_COOKIE)

        // If cookies are not yet available, wait for crypto:keys-ready event (fired by crypto_init_controller)
        if (!publicKeyPem || !privateKeyPem) {
            await new Promise<void>((resolve) => {
                const handler = () => {
                    document.removeEventListener("crypto:keys-ready", handler)
                    resolve()
                }
                document.addEventListener("crypto:keys-ready", handler)
                // Timeout after 5 seconds in case the event never fires
                setTimeout(() => {
                    document.removeEventListener("crypto:keys-ready", handler)
                    resolve()
                }, 5000)
            })
            publicKeyPem = getCookie(PUBLIC_KEY_COOKIE)
            privateKeyPem = getCookie(PRIVATE_KEY_COOKIE)
        }

        if (!publicKeyPem || !privateKeyPem) {
            this.noKeyTarget.classList.remove("hidden")
            return
        }

        try {
            this.publicKey = await importPublicKey(publicKeyPem)
            this.privateKey = await importPrivateKey(privateKeyPem)
        } catch (e) {
            console.error("[PrivateNote] Key import failed:", e)
            this.showStatus("Schlüssel konnten nicht geladen werden.")
            return
        }

        // Enable UI
        this.textareaTarget.disabled = false
        this.saveBtnTarget.disabled = false

        // Auto-resize textarea on input, track unsaved changes
        this.textareaTarget.addEventListener("input", this.autoResize)
        this.textareaTarget.addEventListener("input", this.updateDirtyState)

        // Load and decrypt existing note
        await this.loadNote()
    }

    private autoResize = (): void => {
        const el = this.textareaTarget
        el.style.height = "auto"
        el.style.height = `${el.scrollHeight}px`
    }

    private async loadNote(): Promise<void> {
        try {
            const locale = document.documentElement.lang || "de"
            const response = await fetch(`/${locale}/api/private_note`, {
                headers: { "Accept": "application/json" }
            })
            if (!response.ok) return

            const data = await response.json() as { encrypted_content: string | null }
            if (!data.encrypted_content || !this.privateKey) return

            const plaintext = await decryptHybrid(this.privateKey, data.encrypted_content)
            this.textareaTarget.value = plaintext
            this.savedContent = plaintext
            this.autoResize()
        } catch (e) {
            console.error("[PrivateNote] Load/decrypt failed:", e)
            this.showStatus("Notiz konnte nicht entschlüsselt werden.")
        }
    }

    async save(): Promise<void> {
        if (!this.publicKey) return

        const plaintext = this.textareaTarget.value
        if (!plaintext.trim()) return

        this.indicatorTarget.classList.remove("hidden")
        this.saveBtnTarget.disabled = true

        try {
            const encrypted = await encryptHybrid(this.publicKey, plaintext)
            const locale = document.documentElement.lang || "de"
            const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""

            const response = await fetch(`/${locale}/api/private_note`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": csrfToken
                },
                body: JSON.stringify({ encrypted_content: encrypted })
            })

            if (!response.ok) {
                const err = await response.json() as { error?: string }
                this.showStatus(err.error ?? "Fehler beim Speichern.")
            } else {
                this.savedContent = plaintext
                this.updateDirtyState()
            }
        } catch (e) {
            console.error("[PrivateNote] Save failed:", e)
            this.showStatus("Notiz konnte nicht gespeichert werden.")
        } finally {
            this.indicatorTarget.classList.add("hidden")
            this.saveBtnTarget.disabled = false
        }
    }

    private updateDirtyState = (): void => {
        const isDirty = this.textareaTarget.value !== this.savedContent
        const label = this.saveBtnTarget.dataset.label ?? this.saveBtnTarget.textContent?.replace(" *", "").trim() ?? ""
        if (!this.saveBtnTarget.dataset.label) {
            this.saveBtnTarget.dataset.label = label
        }
        this.saveBtnTarget.textContent = isDirty ? `${label} *` : label
        this.saveBtnTarget.classList.toggle("text-red-700", isDirty)
        this.saveBtnTarget.classList.toggle("font-bold", isDirty)
    }

    private showStatus(message: string): void {
        this.statusTarget.textContent = message
        this.statusTarget.classList.remove("hidden")
    }
}
